import { observer } from 'mobx-react-lite';
import React, { FC } from 'react';
import { IOptionsQueryParams } from '../../../helpers/paramsAutoFiller';
import { useFuncTaskForm } from '../../../hooks/taskFormHooks/useFuncTaskFrom';
import { TaskAddEditContext } from '../../../contexts/TaslAddEditFormContext';
import Loader from '../../Shared/Loader/Loader';

interface TaskAddEditFormProviderProps {
    mode: "create" | "edit";
    optionsQueryParams: IOptionsQueryParams;
    taskId: number | undefined;
    closeFunc: () => void;
    columnId?: number;
    onTaskAction?: () => void;
    projectId?: number;
    permission?: boolean;
    responsibleRequired: boolean;
    parentTaskId?: number;
    children?: React.ReactNode;
    defaultBoardId?: number;
}

const TaskAddEditFormProvider: FC<TaskAddEditFormProviderProps> = ({
    children,
    mode,
    taskId,
    closeFunc,
    columnId,
    onTaskAction,
    projectId,
    permission,
    parentTaskId,
    optionsQueryParams,
    responsibleRequired,
    defaultBoardId
}) => {

    // Hooks
    const TaskFormFunction = useFuncTaskForm({
        mode,
        taskId,
        closeFunc,
        columnId,
        onTaskAction,
        projectId,
        permission,
        parentTaskId,
        optionsQueryParams,
        responsibleRequired,
        defaultBoardId: defaultBoardId
    });

    if (TaskFormFunction.isLoading) {
        return <Loader />;
    }

    return (
        <TaskAddEditContext.Provider value={{
            maxDescription: TaskFormFunction.maxDescription,
            curDescriptionLength: TaskFormFunction.curDescriptionLength,
            setCurDescriptionLength: TaskFormFunction.setCurDescriptionLength,
            setMaxDescription: TaskFormFunction.setMaxDescription,

            taskName: TaskFormFunction.taskName,
            taskDesc: TaskFormFunction.taskDesc,
            taskOldMarkDesc: TaskFormFunction.taskOldMarkDesc,
            taskData: TaskFormFunction.taskData,
            typesPriority: TaskFormFunction.typesPriority,
            persons: TaskFormFunction.persons,
            boards: TaskFormFunction.boards,
            boardId: TaskFormFunction.boardId,
            boardName: TaskFormFunction.boardName,
            responsible: TaskFormFunction.responsible,
            type: TaskFormFunction.type,
            priority: TaskFormFunction.priority,
            tags: TaskFormFunction.tags,
            percentageCompletion: TaskFormFunction.percentageCompletion,
            customHours: TaskFormFunction.customHours,
            customMinutes: TaskFormFunction.customMinutes,
            startDate: TaskFormFunction.startDate,
            endDate: TaskFormFunction.endDate,
            group: TaskFormFunction.group,
            value: TaskFormFunction.value,
            editValue: TaskFormFunction.editValue,
            fileCreate: TaskFormFunction.fileCreate,
            isLoading: TaskFormFunction.isLoading,
            forceLinkName: TaskFormFunction.forceLinkName,
            forceLinkDescription: TaskFormFunction.forceLinkDescription,
            forceLinkTerm: TaskFormFunction.forceLinkTerm,
            forceLinkExecutor: TaskFormFunction.forceLinkExecutor,
            showResponsibleSelector: TaskFormFunction.showResponsibleSelector,
            scrollEnded: TaskFormFunction.scrollEnded,
            tab: TaskFormFunction.tab,
            showGroupSelector: TaskFormFunction.showGroupSelector,
            timeSelectVariant: TaskFormFunction.timeSelectVariant,
            openConfirmWindow: TaskFormFunction.openConfirmWindow,
            openBoardsSelect: TaskFormFunction.openBoardsSelect,
            openOldMark: TaskFormFunction.openOldMark,
            showWarning: TaskFormFunction.showWarning,
            nameError: TaskFormFunction.nameError,
            datesError: TaskFormFunction.datesError,
            priorityError: TaskFormFunction.priorityError,
            boardError: TaskFormFunction.boardError,
            responsibleError: TaskFormFunction.responsibleError,
            typeError: TaskFormFunction.typeError,
            descError: TaskFormFunction.descError,

            // Setters
            setTaskName: TaskFormFunction.setTaskName,
            setTaskDesc: TaskFormFunction.setTaskDesc,
            setTaskOldMarkDesc: TaskFormFunction.setTaskOldMarkDesc,
            setTaskData: TaskFormFunction.setTaskData,
            setTypesPriority: TaskFormFunction.setTypesPriority,
            setShowResponsibleSelector: TaskFormFunction.setShowResponsibleSelector,
            setScrollEnded: TaskFormFunction.setScrollEnded,
            setPersons: TaskFormFunction.setPersons,
            setBoards: TaskFormFunction.setBoards,
            setBoardName: TaskFormFunction.setBoardName,
            setResponsible: TaskFormFunction.setResponsible,
            setType: TaskFormFunction.setType,
            setPriority: TaskFormFunction.setPriority,
            setTags: TaskFormFunction.setTags,
            setPercentageCompletion: TaskFormFunction.setPercentageCompletion,
            setTab: TaskFormFunction.setTab,
            setCustomHours: TaskFormFunction.setCustomHours,
            setCustomMinutes: TaskFormFunction.setCustomMinutes,
            setShowGroupSelector: TaskFormFunction.setShowGroupSelector,
            setStartDate: TaskFormFunction.setStartDate,
            setEndDate: TaskFormFunction.setEndDate,
            setGroup: TaskFormFunction.setGroup,
            setValue: TaskFormFunction.setValue,
            setEditValue: TaskFormFunction.setEditValue,
            setFileCreate: TaskFormFunction.setFileCreate,
            setIsLoading: TaskFormFunction.setIsLoading,
            setTimeSelectVariant: TaskFormFunction.setTimeSelectVariant,
            setOpenConfirmWindow: TaskFormFunction.setOpenConfirmWindow,
            setBoardsSelect: TaskFormFunction.setBoardsSelect,
            setOpenOldMark: TaskFormFunction.setOpenOldMark,
            setShowWarning: TaskFormFunction.setShowWarning,
            setNameError: TaskFormFunction.setNameError,
            setDatesError: TaskFormFunction.setDatesError,
            setPriorityError: TaskFormFunction.setPriorityError,
            setBoardError: TaskFormFunction.setBoardError,
            setResponsibleError: TaskFormFunction.setResponsibleError,
            setTypeError: TaskFormFunction.setTypeError,
            setDescError: TaskFormFunction.setDescError,

            // Function implementations
            calculateRequiredTime: TaskFormFunction.calculateRequiredTime,
            filterTags: TaskFormFunction.filterTags,
            normalizeDescription: TaskFormFunction.normalizeDescription,
            normalizeDate: TaskFormFunction.normalizeDate,
            compileRequest: TaskFormFunction.compileRequest,
            compileSaveParam: TaskFormFunction.compileSaveParam,
            handleTaskAction: TaskFormFunction.handleTaskAction,
            renderTaskOptions: TaskFormFunction.renderTaskOptions,
            taskTypeDisplay: TaskFormFunction.taskTypeDisplay,
            taskPriorityDisplay: TaskFormFunction.taskPriorityDisplay,
            handleTypeSelection: TaskFormFunction.handleTypeSelection,
            handlePrioritySelection: TaskFormFunction.handlePrioritySelection,
            onChangeCustomTime: TaskFormFunction.onChangeCustomTime,
            selectTimeVariant: TaskFormFunction.selectTimeVariant,
            handleGroupSelection: TaskFormFunction.handleGroupSelection,
            onAddTags: TaskFormFunction.onAddTags,
            closeResponsibleSelector: TaskFormFunction.closeResponsibleSelector,
            handleResponsibleSelection: TaskFormFunction.handleResponsibleSelection,
            handleGroupRemove: TaskFormFunction.handleGroupRemove,
            appendPersons: TaskFormFunction.appendPersons,
            onChangeStartDate: TaskFormFunction.onChangeStartDate,
            onChangeEndDate: TaskFormFunction.onChangeEndDate,
            openResponsibleSelector: TaskFormFunction.openResponsibleSelector,
            clearResponsible: TaskFormFunction.clearResponsible,
            onChangeName: TaskFormFunction.onChangeName,
            onConfirm: TaskFormFunction.onConfirm,
            changePage: TaskFormFunction.changePage,
            closeGroupSelector: TaskFormFunction.closeGroupSelector,
            loadPersons: TaskFormFunction.loadPersons,
            removeTags: TaskFormFunction.removeTags,
            openGroupSelector: TaskFormFunction.openGroupSelector,
            getPriority: TaskFormFunction.getPriority,
            fetchData: TaskFormFunction.fetchData,
            closeAndClearTree: TaskFormFunction.closeAndClearTree,
            loadDataStore: TaskFormFunction.loadDataStore,
            fetchInitialData: TaskFormFunction.fetchInitialData,
            loadTaskData: TaskFormFunction.loadTaskData,
            updateTaskFormState: TaskFormFunction.updateTaskFormState,
            checkAndLoadFromParams: TaskFormFunction.checkAndLoadFromParams,
            loadTeamMembers: TaskFormFunction.loadTeamMembers,
            boardIdSave: TaskFormFunction.boardIdSave,
            handleSaveTaskDesk: TaskFormFunction.handleSaveTaskDesk,
            blockDomCustomAllert: TaskFormFunction.blockDomCustomAllert,
            openOldView: TaskFormFunction.openOldView,
            handleClickOutside: TaskFormFunction.handleClickOutside,

            wrapperRef: TaskFormFunction.wrapperRef,
            buttonRef: TaskFormFunction.buttonRef,

            mode: TaskFormFunction.mode,
            optionsQueryParams: TaskFormFunction.optionsQueryParams,
            taskId: TaskFormFunction.taskId,
            closeFunc: TaskFormFunction.closeFunc,
            columnId: TaskFormFunction.columnId,
            onTaskAction: TaskFormFunction.onTaskAction,
            projectId: TaskFormFunction.projectId,
            permission: TaskFormFunction.permission,
            responsibleRequired: TaskFormFunction.responsibleRequired,
            parentTaskId: TaskFormFunction.parentTaskId,
        }}>
            {children}
        </TaskAddEditContext.Provider>
    );
};

export default observer(TaskAddEditFormProvider);